import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Check, ChevronsUpDown, Plus } from 'lucide-react';
import { fetchTreeQuota, listTrees } from '@/api/trees.api';
import { useTreeContextStore } from '@/store/useTreeContextStore';
import { TreeFormModal } from '@/components/Layout/TreeFormModal';
import { Wappen } from '@/components/Layout/Wappen';

// ─── TreeSwitcher ───────────────────────────────────────────────────────────
//
// Umschalter für den aktiven Stammbaum. Beim Wechsel werden ALLE zwischen-
// gespeicherten Daten verworfen – sonst zeigte die App kurzzeitig Personen
// des vorherigen Baums.

interface TreeSwitcherProps {
  /** Vollbreite Darstellung für den mobilen Drawer. */
  variant?: 'topbar' | 'drawer';
  /** Wird nach einem Wechsel aufgerufen (z.B. Drawer schließen). */
  onSwitched?: () => void;
}

export function TreeSwitcher({ variant = 'topbar', onSwitched }: TreeSwitcherProps) {
  const navigate = useNavigate();
  const { activeTreeId, setActiveTree } = useTreeContextStore();
  const [open, setOpen] = useState(false);
  const [zeigeFormular, setZeigeFormular] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { data: trees } = useQuery({
    queryKey: ['trees', 'accessible'],
    queryFn: listTrees,
    staleTime: 60_000,
  });

  // Erst laden, wenn das Menü offen ist – die Zahl interessiert niemanden,
  // bevor er über einen neuen Stammbaum nachdenkt.
  const { data: kontingent } = useQuery({
    queryKey: ['trees', 'quota'],
    queryFn: fetchTreeQuota,
    enabled: open,
    staleTime: 30_000,
  });

  const darfAnlegen = !kontingent || kontingent.unbegrenzt || kontingent.remaining > 0;

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const active = trees?.find((t) => t.id === activeTreeId);

  // Auch bei nur einem Baum sichtbar: seit Nutzer selbst anlegen dürfen, ist
  // dieses Menü ihr einziger Weg dorthin. Vorher blieb es unter zwei Bäumen
  // verborgen – wer einen hatte, konnte nie einen zweiten bekommen.
  if (!trees) return null;

  const handleSelect = (id: string) => {
    setOpen(false);
    if (id === activeTreeId) return;

    // Cache-Reset und Rechte-Nachladen übernimmt zentral der TreeGuard –
    // hier nur umschalten und navigieren.
    setActiveTree(id);
    onSwitched?.();
    navigate('/');
  };

  const isDrawer = variant === 'drawer';

  return (
    <div className={isDrawer ? 'relative w-full' : 'relative'} ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label="Stammbaum wechseln"
        className={`flex items-center gap-2 rounded-md border border-slate-300 bg-white text-sm text-slate-700 hover:bg-slate-50 ${
          isDrawer ? 'w-full px-3 py-2' : 'px-3 py-1.5'
        }`}
      >
        <Wappen url={active?.wappenUrl} groesse={5} />
        <span className={`truncate ${isDrawer ? 'flex-1 text-left' : 'max-w-[140px]'}`}>
          {active?.name ?? 'Stammbaum wählen'}
        </span>
        <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" />
      </button>

      {open && (
        <div
          role="listbox"
          className={`absolute z-50 mt-1 max-h-80 overflow-y-auto rounded-xl border border-slate-200 bg-white py-1.5 shadow-xl ${
            isDrawer ? 'left-0 right-0' : 'left-0 min-w-64'
          }`}
        >
          <p className="px-3 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
            Stammbaum
          </p>
          {trees.map((t) => {
            const isActive = t.id === activeTreeId;
            return (
              <button
                key={t.id}
                role="option"
                aria-selected={isActive}
                onClick={() => handleSelect(t.id)}
                className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm ${
                  isActive ? 'bg-blue-50 text-blue-700' : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                <span className="w-4 shrink-0">
                  {isActive && <Check className="h-4 w-4" aria-hidden="true" />}
                </span>
                <Wappen url={t.wappenUrl} groesse={6} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{t.name}</span>
                  <span className="block text-xs text-slate-400">
                    {t.personCount} {t.personCount === 1 ? 'Person' : 'Personen'}
                  </span>
                </span>
              </button>
            );
          })}

          {/* ─── Anlegen ──────────────────────────────────────────────── */}
          <div className="mt-1 border-t border-slate-100 pt-1">
            {darfAnlegen ? (
              <button
                onClick={() => {
                  setOpen(false);
                  setZeigeFormular(true);
                }}
                className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-blue-700 hover:bg-blue-50"
              >
                <span className="w-4 shrink-0">
                  <Plus className="h-4 w-4" aria-hidden="true" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-medium">Neuer Stammbaum</span>
                  {kontingent && !kontingent.unbegrenzt && (
                    <span className="block text-xs text-slate-400">
                      noch {kontingent.remaining} von {kontingent.limit} frei
                    </span>
                  )}
                </span>
              </button>
            ) : (
              // Kein toter Knopf: wer sein Kontingent ausgeschöpft hat, soll
              // wissen, woran es liegt und wen er fragen kann.
              <p className="px-3 py-2 text-xs leading-relaxed text-slate-400">
                Kontingent ausgeschöpft ({kontingent?.used} von {kontingent?.limit}).
                Für mehr wenden Sie sich an den Betreiber.
              </p>
            )}
          </div>
        </div>
      )}

      {zeigeFormular && <TreeFormModal tree={null} onClose={() => setZeigeFormular(false)} />}
    </div>
  );
}
